import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import apiService from '@/services/api';
import { Plus, Pencil, Trash2, Play, Power, PowerOff, Search } from 'lucide-react';

const Container = styled.div`
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
  table-layout: fixed;
  th, td { border-bottom: 1px solid #e5e7eb; padding: 10px; text-align: left; vertical-align: top; }
  th { background: #f9fafb; font-weight: 600; font-size: 14px; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const TextArea = styled.textarea`
  min-height: 90px;
  padding: 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin: 12px 0;
`;

const Button = styled.button`
  padding: 10px 12px;
  border-radius: 6px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ButtonSmall = styled(Button)`
  padding: 8px 10px;
  font-size: 12px;
`;

// Botões de ação em lote com cores distintas para dar sinal claro (ligar=verde, desligar=cinza, excluir=vermelho)
const BulkButton = styled(ButtonSmall)<{ variant?: 'on' | 'off' | 'danger' }>`
  background: ${({ variant }) =>
    variant === 'on' ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
    : variant === 'danger' ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
    : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'};
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const SearchBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0 10px;
  height: 36px;
  input { border: none; outline: none; height: 34px; min-width: 200px; }
`;

const SelInfo = styled.span`
  font-size: 13px;
  color: #374151;
  font-weight: 600;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const EmailSchedules: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [form, setForm] = useState<any>({
    description: '',
    active: true,
    save_history: false,
    cron_expression: '30 10 * * *',
    time_zone: 'America/Sao_Paulo',
    email_config_id: '',
    recipients: '',
    whatsapp_recipients: '',
    schedule_type: 'email_report',
    tire_alert_window_minutes: 15,
    subject: '',
    body: '',
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [configs, setConfigs] = useState<any[]>([]);
  const [time, setTime] = useState<string>('10:30');
  const [days, setDays] = useState<number[]>([0,1,2,3,4,5,6]); // 0=Dom ... 6=Sab
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<string>('');
  const [bulkBusy, setBulkBusy] = useState<boolean>(false);

  const recarregar = async () => {
    const items = await apiService.listarSchedules();
    setList(items);
  };

  useEffect(() => {
    (async () => {
      try {
        const [items, cfgs] = await Promise.all([
          apiService.listarSchedules(),
          apiService.listarEmailConfigs(),
        ]);
        setList(items);
        setConfigs(cfgs);
      } catch (e: any) {
        toast.error(e?.response?.data?.error || 'Falha ao carregar dados');
      }
    })();
  }, []);

  // Lista filtrada pela busca (por descrição). Vazio = mostra tudo.
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) => String(r.description || '').toLowerCase().includes(q));
  }, [list, filter]);

  const filteredIds = useMemo(() => filtered.map((r) => r.id as number), [filtered]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const selectedCount = selected.size;

  const toggleOne = (id: number, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const toggleAllFiltered = (checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) filteredIds.forEach((id) => next.add(id));
      else filteredIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const limparSelecao = () => setSelected(new Set());

  // Monta o payload de update a partir da linha, no mesmo formato que o formulário envia.
  const payloadFromRow = (row: any, overrides: any) => ({
    description: row.description,
    active: !!row.active,
    save_history: !!row.save_history,
    cron_expression: row.cron_expression,
    time_zone: row.time_zone || 'America/Sao_Paulo',
    email_config_id: row.email_config_id,
    recipients: row.recipients,
    whatsapp_recipients: row.whatsapp_recipients || '',
    schedule_type: row.schedule_type || 'email_report',
    tire_alert_window_minutes: Number(row.tire_alert_window_minutes || 15),
    subject: row.subject,
    body: row.body || '',
    ...overrides,
  });

  const setAtivoEmLote = async (value: boolean) => {
    const alvos = list.filter((r) => selected.has(r.id));
    if (!alvos.length) { toast.error('Nenhum agendamento selecionado'); return; }
    const rotulo = value ? 'ATIVAR' : 'DESATIVAR';
    if (!confirm(`Confirma ${rotulo} ${alvos.length} agendamento(s) selecionado(s)?`)) return;
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const row of alvos) {
      try {
        await apiService.atualizarSchedule(row.id, payloadFromRow(row, { active: value }));
        ok++;
      } catch { fail++; }
    }
    setBulkBusy(false);
    await recarregar();
    limparSelecao();
    if (fail) toast.error(`${ok} atualizado(s), ${fail} falhou(aram)`);
    else toast.success(`${ok} agendamento(s) ${value ? 'ativado(s)' : 'desativado(s)'}`);
  };

  const excluirEmLote = async () => {
    const alvos = list.filter((r) => selected.has(r.id));
    if (!alvos.length) { toast.error('Nenhum agendamento selecionado'); return; }
    if (!confirm(`Confirma EXCLUIR ${alvos.length} agendamento(s)? Esta ação não pode ser desfeita.`)) return;
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const row of alvos) {
      try {
        await apiService.excluirSchedule(row.id);
        ok++;
      } catch { fail++; }
    }
    setBulkBusy(false);
    await recarregar();
    limparSelecao();
    if (fail) toast.error(`${ok} excluído(s), ${fail} falhou(aram)`);
    else toast.success(`${ok} agendamento(s) excluído(s)`);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as any;
    const checked = (e.target as HTMLInputElement).checked;
    setForm((prev: any) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNovo = () => {
    setEditingId(null);
    setForm({ description: '', active: true, save_history: false, cron_expression: '30 10 * * *', time_zone: 'America/Sao_Paulo', email_config_id: '', recipients: '', whatsapp_recipients: '', schedule_type: 'email_report', tire_alert_window_minutes: 15, subject: '', body: '' });
    setTime('10:30');
    setDays([0,1,2,3,4,5,6]);
  };

  const handleSalvar = async () => {
    try {
      // monta cron a partir do time/days; alertas de pneus rodam a cada 15 minutos
      const [hhStr, mmStr] = (time || '00:00').split(':');
      const hour = String(hhStr || '00');
      const minute = String(mmStr || '00');
      const dow = Array.isArray(days) && days.length > 0 ? days.sort((a,b)=>a-b).join(',') : '*';
      const isTireAlert = form.schedule_type === 'tire_requirement_alert';
      const cron = isTireAlert ? '*/15 * * * *' : `${minute} ${hour} * * ${dow}`;
      const payload = {
        ...form,
        cron_expression: cron,
        time_zone: 'America/Sao_Paulo',
        tire_alert_window_minutes: isTireAlert ? 15 : Number(form.tire_alert_window_minutes || 15),
        subject: isTireAlert && !form.subject ? 'Alerta de pneus sem produtos obrigatórios' : form.subject,
      };
      if (editingId) {
        await apiService.atualizarSchedule(editingId, payload);
      } else {
        await apiService.criarSchedule(payload);
      }
      toast.success('Agendamento salvo');
      setEditingId(null);
      setForm({ ...form, body: '' });
      const items = await apiService.listarSchedules();
      setList(items);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao salvar');
    }
  };

  const handleEditar = (row: any) => {
    setEditingId(row.id);
    setForm({
      description: row.description,
      active: !!row.active,
      save_history: !!row.save_history,
      cron_expression: row.cron_expression,
      time_zone: row.time_zone || 'America/Sao_Paulo',
      email_config_id: row.email_config_id,
      recipients: row.recipients,
      whatsapp_recipients: row.whatsapp_recipients || '',
      schedule_type: row.schedule_type || 'email_report',
      tire_alert_window_minutes: Number(row.tire_alert_window_minutes || 15),
      subject: row.subject,
      body: row.body || '',
    });
    // parse cron -> time/days
    const parts = String(row.cron_expression || '').trim().split(/\s+/);
    if ((row.schedule_type || 'email_report') === 'tire_requirement_alert') {
      setTime('00:00');
      setDays([0,1,2,3,4,5,6]);
    } else if (parts.length >= 5) {
      const minute = parts[0];
      const hour = parts[1];
      const dow = parts[4];
      setTime(`${hour.padStart(2,'0')}:${minute.padStart(2,'0')}`);
      if (dow === '*' || dow === '*/1') setDays([0,1,2,3,4,5,6]);
      else {
        const values = dow.split(',').map((v: string) => parseInt(v, 10)).filter((n: number) => !isNaN(n));
        setDays(values.length ? values : [0,1,2,3,4,5,6]);
      }
    } else {
      setTime('10:30');
      setDays([0,1,2,3,4,5,6]);
    }
  };

  const handleExcluir = async (id: number) => {
    if (!confirm('Confirma excluir este agendamento?')) return;
    await apiService.excluirSchedule(id);
    toast.success('Agendamento excluído');
    const items = await apiService.listarSchedules();
    setList(items);
  };

  const handleDispararAgora = async (id: number) => {
    try {
      const res = await apiService.dispararScheduleAgora(id);
      // Log detalhado no console do navegador com diagnóstico do backend
      if (res?.diagnostics) {
        try {
          const reqId = res?.requestId || '(sem-id)';
          // Agrupa os logs para facilitar leitura
          console.groupCollapsed(`[Agendamentos] Disparo #${id} - diagnóstico (requestId=${reqId})`);
          console.info('Email:', res.diagnostics.email);
          console.info('WhatsApp:', res.diagnostics.whatsapp);
          if (res?.diagnostics?.preview) {
            console.info('Preview: processamento em background. Acompanhe pelos logs do servidor usando o requestId.');
          }
          console.groupEnd();
          const emailWillSend = res?.diagnostics?.email?.willSend;
          const whatsWillSend = res?.diagnostics?.whatsapp?.willSend;
          const emailZero = (typeof emailWillSend === 'number') && emailWillSend === 0;
          const whatsZero = (typeof whatsWillSend === 'number') && whatsWillSend === 0;
          if (emailZero && whatsZero) {
            const motivoEmail = res?.diagnostics?.email?.reasonSkip || 'sem_motivo_definido';
            const motivoWhats = res?.diagnostics?.whatsapp?.reasonSkip || 'sem_motivo_definido';
            console.warn(`[Agendamentos] Nenhuma mensagem enviada. Motivos: email=${motivoEmail}, whatsapp=${motivoWhats}`);
          }
        } catch {}
      }
      toast.success('Disparo solicitado (verifique o console para diagnóstico)');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao disparar');
    }
  };

  return (
    <Container>
      <Card>
        <Title>Agendamentos de E-mail</Title>
        <Actions>
          <Button onClick={handleNovo}><Plus size={16} /> Novo</Button>
        </Actions>
        <Grid>
          <Field>
            <Label>Descrição</Label>
            <Input name="description" value={form.description} onChange={handleChange} />
          </Field>
          <Field>
            <Label>Ativo</Label>
            <CheckboxRow>
              <input type="checkbox" name="active" checked={!!form.active} onChange={handleChange} /> Ativo
            </CheckboxRow>
          </Field>
          <Field>
            <Label>Tipo de agendamento</Label>
            <select name="schedule_type" value={form.schedule_type || 'email_report'} onChange={handleChange} style={{ height: 36, border: '1px solid #d1d5db', borderRadius: 6, padding: '0 10px', background: '#fff' }}>
              <option value="email_report">Relatório por e-mail/WhatsApp</option>
              <option value="tire_requirement_alert">Alerta de pneus sem produtos obrigatórios</option>
            </select>
          </Field>
          <Field>
            <Label>Salvar Histórico</Label>
            <CheckboxRow>
              <input type="checkbox" name="save_history" checked={!!form.save_history} onChange={handleChange} /> Salvar Histórico
            </CheckboxRow>
          </Field>
          {form.schedule_type === 'tire_requirement_alert' ? (
            <Field>
              <Label>Frequência</Label>
              <Input value="A cada 15 minutos, consultando vendas dos últimos 15 minutos" readOnly />
            </Field>
          ) : (
            <>
              <Field>
                <Label>Horário</Label>
                <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </Field>
              <Field>
                <Label>Dias da semana</Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    { v:0, t:'Dom' },{ v:1, t:'Seg' },{ v:2, t:'Ter' },{ v:3, t:'Qua' },{ v:4, t:'Qui' },{ v:5, t:'Sex' },{ v:6, t:'Sáb' }
                  ].map(d => (
                    <CheckboxRow key={d.v} style={{ minWidth: 70 }}>
                      <input type="checkbox" checked={days.includes(d.v)} onChange={(e) => {
                        setDays(prev => e.target.checked ? Array.from(new Set([...prev, d.v])) : prev.filter(x => x !== d.v));
                      }} /> {d.t}
                    </CheckboxRow>
                  ))}
                </div>
              </Field>
            </>
          )}
          <Field>
            <Label>Configuração de E-mail</Label>
            <select name="email_config_id" value={form.email_config_id} onChange={handleChange} style={{ height: 36, border: '1px solid #d1d5db', borderRadius: 6, padding: '0 10px', background: '#fff' }}>
              <option value="">Selecione...</option>
              {configs.map(c => (
                <option key={c.id} value={c.id}>{c.description}</option>
              ))}
            </select>
          </Field>
          <Field>
            <Label>Destinatários (separados por vírgula)</Label>
            <Input name="recipients" value={form.recipients} onChange={handleChange} />
          </Field>
          <Field>
            <Label>Destinatários Whatsapp (separados por vírgula ou SELECT)</Label>
            <Input name="whatsapp_recipients" value={form.whatsapp_recipients} onChange={handleChange} placeholder="+5511999999999,+351968483466 ou SELECT phone AS whatsapp FROM whatsapp_recipients WHERE active=1" />
          </Field>
          <Field>
            <Label>Assunto</Label>
            <Input name="subject" value={form.subject} onChange={handleChange} />
          </Field>
          <Field style={{ gridColumn: '1 / -1' }}>
            <Label>{form.schedule_type === 'tire_requirement_alert' ? 'Observação interna (opcional)' : 'Corpo do E-mail (texto simples)'}</Label>
            <TextArea name="body" value={form.body} onChange={handleChange} disabled={form.schedule_type === 'tire_requirement_alert'} />
          </Field>
        </Grid>
        <Actions>
          <Button onClick={handleSalvar}>Salvar</Button>
        </Actions>

        {/* Barra de ações em lote: buscar, selecionar e ligar/desligar/excluir vários de uma vez */}
        <BulkBar>
          <SearchBox>
            <Search size={16} color="#6b7280" />
            <input
              placeholder="Buscar por descrição (ex: estoque)"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </SearchBox>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={(e) => toggleAllFiltered(e.target.checked)}
            /> Selecionar todos{filter.trim() ? ` (${filteredIds.length} do filtro)` : ` (${filteredIds.length})`}
          </CheckboxRow>
          <SelInfo>{selectedCount} selecionado(s)</SelInfo>
          <BulkButton variant="on" disabled={bulkBusy || selectedCount === 0} onClick={() => setAtivoEmLote(true)}>
            <Power size={16} /> Ativar selecionados
          </BulkButton>
          <BulkButton variant="off" disabled={bulkBusy || selectedCount === 0} onClick={() => setAtivoEmLote(false)}>
            <PowerOff size={16} /> Desativar selecionados
          </BulkButton>
          <BulkButton variant="danger" disabled={bulkBusy || selectedCount === 0} onClick={excluirEmLote}>
            <Trash2 size={16} /> Excluir selecionados
          </BulkButton>
          {selectedCount > 0 && (
            <ButtonSmall style={{ background: '#e5e7eb', color: '#374151' }} onClick={limparSelecao}>Limpar</ButtonSmall>
          )}
        </BulkBar>

        <div style={{ overflowX: 'auto', marginTop: 8 }}>
          <Table>
            <colgroup>
              <col style={{ width: 40 }} />
              <col style={{ width: 260 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 144 }} />
              <col style={{ width: 128 }} />
              <col style={{ width: 210 }} />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={(e) => toggleAllFiltered(e.target.checked)}
                    title="Selecionar todos"
                  />
                </th>
                <th>Descrição</th>
                <th>Ativo</th>
                <th>Tipo</th>
                <th>Histórico</th>
                <th>Cron</th>
                <th>Config</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} style={selected.has(row.id) ? { background: '#eef2ff' } : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={(e) => toggleOne(row.id, e.target.checked)}
                    />
                  </td>
                  <td>{row.description}</td>
                  <td>{(row.active ? 1 : 0) === 1 ? 'Sim' : 'Não'}</td>
                  <td>{row.schedule_type === 'tire_requirement_alert' ? 'Alerta pneus' : 'Relatório'}</td>
                  <td>{(row.save_history ? 1 : 0) === 1 ? 'Sim' : 'Não'}</td>
                  <td>{row.cron_expression}</td>
                  <td>{configs.find(c => c.id === row.email_config_id)?.description || row.email_config_id}</td>
                  <td>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                      <ButtonSmall style={{ width: '100%' }} onClick={() => handleEditar(row)}>
                        <Pencil size={16} /> Editar
                      </ButtonSmall>
                      <ButtonSmall style={{ width: '100%' }} onClick={() => handleExcluir(row.id)}>
                        <Trash2 size={16} /> Excluir
                      </ButtonSmall>
                      <ButtonSmall style={{ width: '100%', gridColumn: '1 / -1' }} onClick={() => handleDispararAgora(row.id)}>
                        <Play size={16} /> Disparar agora
                      </ButtonSmall>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card>
    </Container>
  );
};

export default EmailSchedules;
